import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Tooltip, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import {
  DEFAULT_ANIMATION_DURATION,
  DEFAULT_FILLED_COLOR,
  DEFAULT_FILLED_COLOR_END,
  DEFAULT_SIZE,
  DEFAULT_TICK_COUNT,
  DEFAULT_TICK_GAP,
  GEOMETRY,
  MAX_TICK_GAP,
  MIN_SIZE_FOR_LABEL,
} from './constants';
import type { SodRadialGaugeProps } from './types';
import {
  defaultFormatValue,
  easeOut,
  getCSSVariableValue,
  lerpHex,
  lerpMultipleColors,
  polarToCartesian,
  prefersReducedMotion,
} from './utils';

import './SodRadialGauge.css';

function SodRadialGauge({
  value,
  total,
  label,
  formatValue = defaultFormatValue,
  size = DEFAULT_SIZE,
  tickCount = DEFAULT_TICK_COUNT,
  filledColor = DEFAULT_FILLED_COLOR,
  filledColorEnd = DEFAULT_FILLED_COLOR_END,
  filledColorStops,
  animationDuration = DEFAULT_ANIMATION_DURATION,
  tickGap = DEFAULT_TICK_GAP,
  rotation = 0,
  onGaugeClick,
  'aria-label': ariaLabel,
  className,
}: SodRadialGaugeProps) {
  const safeTotal = total > 0 ? total : 0;
  const safeValue = Math.max(0, Math.min(value, safeTotal));
  const percent =
    safeTotal === 0 ? 0 : Math.round((safeValue / safeTotal) * 100);
  const filledCount = Math.round((tickCount * percent) / 100);

  // Resolve CSS variables to actual color values
  const resolvedFilledColor = filledColor.includes('var(')
    ? getCSSVariableValue('--sodRadialGauge-filledColor-start')
    : filledColor;
  const resolvedFilledColorEnd = filledColorEnd.includes('var(')
    ? getCSSVariableValue('--sodRadialGauge-filledColor-end')
    : filledColorEnd;

  // Resolve color stops if they contain CSS variables
  const resolvedFilledColorStops = filledColorStops?.map(([pos, color]) => {
    const resolvedColor = color.includes('var(')
      ? getCSSVariableValue('--sodRadialGauge-filledColor-start')
      : color;

    return [pos, resolvedColor] as [number, string];
  });

  const skipAnimation = prefersReducedMotion();
  const frameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const [visibleFilled, setVisibleFilled] = useState(
    skipAnimation ? filledCount : 0
  );
  const [animateKey, setAnimateKey] = useState(0);

  // Determine if we're using multi-color stops or simple 2-color gradient
  const useColorStops = !!resolvedFilledColorStops;

  const animate = useCallback(
    (timestamp: number): void => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const rawProgress = Math.min(elapsed / animationDuration, 1);

      setVisibleFilled(Math.round(easeOut(rawProgress) * filledCount));

      if (rawProgress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    },
    [filledCount, animationDuration]
  );

  useEffect(() => {
    if (skipAnimation) {
      setVisibleFilled(filledCount);

      return undefined;
    }

    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
    }

    startTimeRef.current = null;
    setVisibleFilled(0);
    setAnimateKey((k) => k + 1);
    frameRef.current = requestAnimationFrame(animate);

    return (): void => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [animate, filledCount, skipAnimation]);

  const centerX = size / 2;
  const centerY = size * GEOMETRY.centerYRatio;
  const innerRadius = size * GEOMETRY.innerRadiusRatio;
  const outerRadius = innerRadius + size * GEOMETRY.tickLengthRatio;
  const svgHeight = size * GEOMETRY.svgHeightRatio;
  const delayPerTick = filledCount > 0 ? animationDuration / filledCount : 0;
  const tickStrokeWidth =
    size *
    GEOMETRY.strokeWidthRatio *
    (1 - Math.min(Math.max(tickGap, 0), MAX_TICK_GAP));

  const tickGeometry = useMemo(
    () =>
      Array.from({ length: tickCount }, (_, i) => {
        const angle = (i / (tickCount - 1)) * 180;
        // For filled ticks, calculate position within the filled portion (0 to 1)
        // For unfilled ticks, use position across entire gauge
        const positionInGauge = i / (tickCount - 1);
        const isFilled = positionInGauge <= percent / 100;
        // When using color stops, map t as position within filled portion (0 to 1)
        // This ensures color gradient spans from first to last filled tick
        const t =
          useColorStops && isFilled
            ? positionInGauge / (percent / 100)
            : positionInGauge;
        const tickColor = useColorStops
          ? lerpMultipleColors(resolvedFilledColorStops, t)
          : lerpHex(resolvedFilledColor, resolvedFilledColorEnd, t);

        return {
          inner: polarToCartesian(centerX, centerY, innerRadius, angle),
          outer: polarToCartesian(centerX, centerY, outerRadius, angle),
          filledColor: tickColor,
        };
      }),
    [
      tickCount,
      centerX,
      centerY,
      innerRadius,
      outerRadius,
      useColorStops,
      resolvedFilledColorStops,
      resolvedFilledColor,
      resolvedFilledColorEnd,
      percent,
    ]
  );

  const formattedValue = formatValue(safeValue);
  const formattedTotal = formatValue(safeTotal);
  const autoAriaLabel = `${
    typeof label === 'string' ? label : 'Gauge'
  }: ${formattedValue} of ${formattedTotal} (${percent}% used)`;
  const descId = useRef(
    `sodRadialGauge-desc-${Math.random().toString(36).slice(2, 7)}`
  ).current;
  const descriptionText = `Semi-circular gauge showing ${String(
    formattedValue
  )} out of ${String(formattedTotal)} (${String(percent)}% used)`;

  return (
    <Tooltip
      trigger={
        <div
          className={cx('SodRadialGauge', className)}
          data-rotation={rotation}>
          <svg
            role='meter'
            aria-valuenow={safeValue}
            aria-valuemin={0}
            aria-valuemax={safeTotal}
            aria-label={ariaLabel ?? autoAriaLabel}
            aria-describedby={descId}
            tabIndex={0}
            width={size}
            height={svgHeight}
            viewBox={`0 0 ${size} ${svgHeight}`}
            preserveAspectRatio='xMidYMid meet'
            className='SodRadialGauge-svg'
            style={{ cursor: onGaugeClick ? 'pointer' : 'default' }}
            onClick={() =>
              onGaugeClick?.({
                value,
                total,
                safeValue,
                safeTotal,
                percent,
                filledCount,
                formattedValue,
                formattedTotal,
              })
            }>
            <desc id={descId}>{descriptionText}</desc>

            <g transform={`rotate(${rotation} ${centerX} ${centerY})`}>
              {tickGeometry.map((tick, index) => {
                const isFilled = index < visibleFilled;
                const tickKey = `tick-${animateKey}-${index}`;

                return isFilled ? (
                  <line
                    key={tickKey}
                    x1={tick.inner.x}
                    y1={tick.inner.y}
                    x2={tick.outer.x}
                    y2={tick.outer.y}
                    strokeWidth={tickStrokeWidth}
                    strokeLinecap='round'
                    stroke={tick.filledColor}
                    className='SodRadialGauge-tick SodRadialGauge-tick--filled'
                    style={
                      !skipAnimation
                        ? {
                            animationDelay: `${index * delayPerTick}ms`,
                            animationDuration: `${delayPerTick + 80}ms`,
                          }
                        : undefined
                    }
                  />
                ) : (
                  <line
                    key={tickKey}
                    x1={tick.inner.x}
                    y1={tick.inner.y}
                    x2={tick.outer.x}
                    y2={tick.outer.y}
                    strokeWidth={tickStrokeWidth}
                    strokeLinecap='round'
                    className='SodRadialGauge-tick SodRadialGauge-tick--unfilled'
                  />
                );
              })}
            </g>
          </svg>

          {size >= MIN_SIZE_FOR_LABEL && (
            <div className='SodRadialGauge-textOverlay'>
              <Typography kind='h1' className='SodRadialGauge-valueText'>
                {formattedValue}
              </Typography>
              {label && (
                <div className='SodRadialGauge-labelContainer'>
                  {typeof label === 'string'
                    ? label.split(' ').map((word) => (
                        <Typography
                          key={`label-word-${word}`}
                          kind='body2'
                          className='SodRadialGauge-labelText'>
                          {word}
                        </Typography>
                      ))
                    : label.map((line) => (
                        <Typography
                          key={`label-line-${line}`}
                          kind='body2'
                          className='SodRadialGauge-labelText'>
                          {line}
                        </Typography>
                      ))}
                </div>
              )}
            </div>
          )}
        </div>
      }
      text={`${percent}% used`}
      placement='top'
      isPortalEnabled
    />
  );
}

export default memo(SodRadialGauge);
